import React from 'react';
import { useFormContext, useController } from 'react-hook-form';

import { ComboBox } from '@epoch-ui/components/ComboBox/ComboBox';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator
} from '@epoch-ui/components/Command/command';
import {
  FormField,
  FormControl,
  FormDescription,
  FormLabel,
  FormItem,
  FormMessage
} from '@epoch-ui/components/Form/form';

// =============================================================================
// INTERNAL CONTEXT FOR COMBOBOX FIELD
// =============================================================================

const ComboBoxFieldContext = React.createContext<{
  fieldName: string;
  closeComboBox?: () => void;
} | null>(null);

const useComboBoxField = (): {
  fieldName: string;
  closeComboBox?: () => void;
} => {
  const context = React.useContext(ComboBoxFieldContext);
  if (!context) {
    throw new Error('useComboBoxField must be used within a ComboBoxFieldContext');
  }
  return context;
};

// =============================================================================
// COMBO BOX FIELD FORM COMPONENT
// =============================================================================

export interface ComboBoxFieldProps
  extends Omit<
    React.ComponentPropsWithRef<typeof ComboBox>,
    'isOpen' | 'onOpenChange' | 'triggerContent'
  > {
  name: string;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  defaultOpen?: boolean;
  triggerContent: React.ReactNode | ((value: any) => React.ReactNode);
}

const ComboBoxFieldRoot: React.FC<ComboBoxFieldProps> = ({
  name,
  label,
  description,
  defaultOpen = false,
  children,
  triggerContent,
  className,
  ...props
}) => {
  const { control } = useFormContext();
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const contextValue = React.useMemo(
    () => ({
      fieldName: name,
      closeComboBox: (): void => setIsOpen(false)
    }),
    [name]
  );

  return (
    <ComboBoxFieldContext.Provider value={contextValue}>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }): React.ReactElement => (
          <FormItem className={className}>
            {label && <FormLabel className="tw-mb-0">{label}</FormLabel>}
            {description && <FormDescription className="tw-m-0">{description}</FormDescription>}
            <FormControl className="tw-mt-2">
              <ComboBox
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                error={!!fieldState.error}
                {...props}
                triggerContent={
                  typeof triggerContent === 'function'
                    ? triggerContent(field.value)
                    : triggerContent
                }
              >
                {children}
              </ComboBox>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </ComboBoxFieldContext.Provider>
  );
};

// =============================================================================
// ENHANCED COMMAND COMPONENTS FOR COMBOBOX FIELD
// =============================================================================

// Enhanced CommandItem that works with ComboBoxField context
const Item = React.forwardRef<
  React.ElementRef<typeof CommandItem>,
  React.ComponentPropsWithoutRef<typeof CommandItem> & {
    value?: any;
    onSelect?: (value: any) => void;
  }
>(({ value, onSelect, children, ...props }, ref): React.ReactElement => {
  const { fieldName, closeComboBox } = useComboBoxField();
  const { field } = useController({ name: fieldName });

  const handleSelect = React.useCallback((): void => {
    field?.onChange(value);
    if (onSelect) {
      onSelect(value);
    }
    // Close the ComboBox after selection
    if (closeComboBox) {
      closeComboBox();
    }
  }, [value, onSelect, field?.onChange, closeComboBox]);

  return (
    <CommandItem ref={ref} onSelect={handleSelect} {...props}>
      {children}
    </CommandItem>
  );
});

Item.displayName = 'ComboBoxField.Item';

// Create compound component with Object.assign
const ComboBoxField = Object.assign(ComboBoxFieldRoot, {
  Item,
  Command,
  Input: CommandInput,
  List: CommandList,
  Empty: CommandEmpty,
  Group: CommandGroup,
  Shortcut: CommandShortcut,
  Separator: CommandSeparator
});

export { ComboBoxField };
